import { useCallback, useEffect, useId, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/client/components/layout/header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { ScrollArea } from "@/client/components/ui/scroll-area";
import { api, describeError, unwrap } from "@/client/lib/api";
import { authClient } from "@/client/lib/auth-client";
import { useSession } from "@/client/hooks/use-session";

export const Route = createFileRoute("/admin")({
  component: AdminPage,
});

interface AdminUser {
  id: string;
  name: string;
  email: string;
  role?: string | null;
  banned?: boolean | null;
  createdAt: Date | string;
}

interface Invite {
  id: string;
  email: string;
  status: "pending" | "consumed" | "revoked" | "expired";
  expiresAt: Date | string;
  createdAt: Date | string;
}

const INVITE_STATUS: Record<Invite["status"], string> = {
  pending: "Waiting",
  consumed: "Used",
  revoked: "Revoked",
  expired: "Expired",
};

function formatDate(value: Date | string) {
  return new Date(value).toLocaleDateString();
}

function AdminPage() {
  const { isAdmin, user } = useSession();

  if (!isAdmin) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="container mx-auto max-w-3xl px-4 py-8">
          <p className="text-muted-foreground">
            Only the administrator can open this page. <Link to="/" className="underline">Back to your stories</Link>
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <Header />
      <ScrollArea className="min-h-0 flex-1">
        <main className="container mx-auto grid max-w-3xl gap-10 px-4 py-8">
          <div>
            <h1 className="text-3xl font-bold">Administration</h1>
            <p className="mt-1 text-muted-foreground">
              Admit people to this instance and suspend accounts. You can't see anyone's stories from here.
            </p>
          </div>
          <Invites />
          <Users currentUserId={user!.id} />
        </main>
      </ScrollArea>
    </div>
  );
}

function Invites() {
  const id = useId();
  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; setupUrl: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setInvites((await unwrap(api.api.admin.invites.get())) as Invite[]);
    } catch (error) {
      toast.error("Couldn't load invites", { description: describeError(error) });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy("create");
    try {
      const invite = await unwrap(api.api.admin.invites.post({ email }));
      setCreated({ email: invite.email, setupUrl: invite.setupUrl });
      setCopied(false);
      setEmail("");
      await load();
    } catch (error) {
      toast.error("Couldn't create the invite", { description: describeError(error) });
    } finally {
      setBusy(null);
    }
  };

  const handleCopy = async () => {
    if (!created) return;
    await navigator.clipboard.writeText(created.setupUrl);
    setCopied(true);
  };

  const handleRevoke = async (invite: Invite) => {
    setBusy(invite.id);
    try {
      await unwrap(api.api.admin.invites({ id: invite.id }).revoke.post());
      if (created?.email === invite.email) setCreated(null);
      await load();
    } catch (error) {
      toast.error("Couldn't revoke the invite", { description: describeError(error) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="grid gap-4">
      <div>
        <h2 className="text-xl font-semibold">Setup links</h2>
        <p className="text-sm text-muted-foreground">
          A setup link lets one person create an account with the email you enter. It works once and
          expires after seven days. Nothing is emailed, so send it to them yourself.
        </p>
      </div>
      <form onSubmit={handleCreate} className="flex items-end gap-2">
        <div className="grid flex-1 gap-2">
          <Label htmlFor={`${id}-email`}>Email</Label>
          <Input
            id={`${id}-email`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="friend@example.com"
            required
          />
        </div>
        <Button type="submit" disabled={busy !== null || !email.trim()}>
          {busy === "create" && <Loader2 className="animate-spin" />}
          Create link
        </Button>
      </form>

      {created && (
        <div className="grid gap-2 rounded-md border p-3">
          <p className="text-sm">
            Setup link for <span className="font-medium">{created.email}</span>. It's shown only once.
          </p>
          <div className="flex gap-2">
            <Input readOnly value={created.setupUrl} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
            <Button type="button" variant="outline" onClick={() => void handleCopy()}>
              {copied ? <Check /> : <Copy />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      )}

      {invites === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : invites.length === 0 ? (
        <p className="text-sm text-muted-foreground">No setup links yet.</p>
      ) : (
        <ul className="grid divide-y rounded-md border">
          {invites.map((invite) => (
            <li key={invite.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="min-w-0 truncate">{invite.email}</span>
              <span className="flex shrink-0 items-center gap-3">
                <span className="text-xs text-muted-foreground">
                  {invite.status === "pending" ? `Expires ${formatDate(invite.expiresAt)}` : formatDate(invite.createdAt)}
                </span>
                <Badge variant={invite.status === "pending" ? "default" : "secondary"}>
                  {INVITE_STATUS[invite.status]}
                </Badge>
                {invite.status === "pending" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7"
                    disabled={busy !== null}
                    onClick={() => void handleRevoke(invite)}
                  >
                    Revoke
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Users({ currentUserId }: { currentUserId: string }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await authClient.admin.listUsers({ query: { limit: 200 } });
    if (error) {
      toast.error("Couldn't load users", { description: error.message });
      return;
    }
    setUsers(data.users as AdminUser[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Suspension goes through banUser, which also ends the user's sessions
  const setSuspended = async (target: AdminUser, suspended: boolean) => {
    setBusy(target.id);
    const { error } = suspended
      ? await authClient.admin.banUser({ userId: target.id })
      : await authClient.admin.unbanUser({ userId: target.id });
    setBusy(null);
    if (error) {
      toast.error(`Couldn't ${suspended ? "suspend" : "reactivate"} ${target.name}`, {
        description: error.message,
      });
      return;
    }
    toast.success(suspended ? `${target.name} is suspended` : `${target.name} is active again`);
    await load();
  };

  return (
    <section className="grid gap-4">
      <div>
        <h2 className="text-xl font-semibold">Users</h2>
        <p className="text-sm text-muted-foreground">
          Suspending someone signs them out everywhere and blocks their next request. Their stories and
          memberships stay as they are.
        </p>
      </div>
      {users === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      ) : (
        <ul className="grid divide-y rounded-md border">
          {users.map((u) => (
            <li key={u.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="font-medium">{u.name}</span>
                <span className="ml-2 text-muted-foreground">{u.email}</span>
              </span>
              <span className="flex shrink-0 items-center gap-3">
                {u.role === "admin" && <Badge variant="secondary">Administrator</Badge>}
                <Badge variant={u.banned ? "destructive" : "outline"}>{u.banned ? "Suspended" : "Active"}</Badge>
                {u.id !== currentUserId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7"
                    disabled={busy !== null}
                    onClick={() => void setSuspended(u, !u.banned)}
                  >
                    {busy === u.id && <Loader2 className="animate-spin" />}
                    {u.banned ? "Reactivate" : "Suspend"}
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
